import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { LIEN_DOC_TITLE, LIEN_WAIVER_KINDS, LIEN_WAIVER_WHEN, paths, type Agreement, type Invoice, type LienWaiverKind } from "@clevis/shared-types";
import { Button, Input, Label, cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { call } from "../lib/callFunction";
import { PresentAgreement } from "./JobAgreements";

// Invoices → Lien waiver: one of Nevada's four statutory waivers, filled from
// this invoice (property, customer, invoice number, amount), signed for the
// company, then sent as a PDF. Unconditional ones are only for money received.

type Row = Agreement & { id: string };
const dollars = (c: number) => (c / 100).toFixed(2);
const toCents = (s: string) => Math.round(Number(s.replace(/[$,\s]/g, "")) * 100);

function suggested(inv: Invoice): LienWaiverKind {
  if (inv.status === "paid") return "unconditional_final";
  if (inv.amountPaidCents > 0) return "unconditional_progress";
  return "conditional_final";
}

export function LienWaivers({ tenantId, invoice, companyName }: { tenantId: string; invoice: Invoice & { id: string }; companyName: string }) {
  const [rows, setRows] = useState<Row[]>([]);
  const [kind, setKind] = useState<LienWaiverKind>(() => suggested(invoice));
  const unconditional = kind.startsWith("unconditional");
  const final = kind.endsWith("final");
  const balance = invoice.totalCents - invoice.amountPaidCents;
  const [amount, setAmount] = useState(() => dollars(suggested(invoice).startsWith("unconditional") ? invoice.amountPaidCents : balance));
  const [disputed, setDisputed] = useState("0.00");
  const [period, setPeriod] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [signing, setSigning] = useState<Row | null>(null);

  useEffect(
    () =>
      onSnapshot(
        query(collection(db, paths.tenantAgreements(tenantId)), where("invoiceId", "==", invoice.id)),
        (s) => setRows(s.docs.map((d) => ({ id: d.id, ...(d.data() as Agreement) })).sort((a, b) => b.createdAt - a.createdAt)),
        () => setRows([]),
      ),
    [tenantId, invoice.id],
  );

  function pick(k: LienWaiverKind) {
    setKind(k);
    setAmount(dollars(k.startsWith("unconditional") ? invoice.amountPaidCents : balance > 0 ? balance : invoice.totalCents));
    setError("");
  }
  async function create() {
    const cents = toCents(amount);
    if (!(cents > 0)) return setError("Enter the payment amount.");
    setBusy(true);
    setError("");
    try {
      await call("createLienWaiver", { tenantId, invoiceId: invoice.id, kind, amountCents: cents, disputedCents: final ? Math.max(0, toCents(disputed) || 0) : 0, paymentPeriod: kind === "conditional_final" ? period : "" });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function pdf(r: Row) {
    try {
      const res = await call<{ base64: string; filename: string }>("getAgreementPdf", { tenantId, agreementId: r.id }, { mutation: false });
      const a = document.createElement("a");
      a.href = `data:application/pdf;base64,${res.base64}`;
      a.download = res.filename;
      a.click();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <div className="mx-4 mb-2 space-y-2 rounded-lg border border-steel/20 bg-cream/40 p-3">
      <div className="text-sm font-semibold text-ink">Lien waiver for invoice #{invoice.number} (Nevada)</div>
      <div className="grid gap-1.5 sm:grid-cols-2">
        {LIEN_WAIVER_KINDS.map((k) => {
          const blocked = k.startsWith("unconditional") && (invoice.amountPaidCents === 0 || (k === "unconditional_final" && invoice.status !== "paid"));
          return (
            <button
              key={k}
              type="button"
              disabled={blocked}
              className={cn("rounded-lg border bg-white p-2 text-left text-xs", kind === k ? "border-brandorange ring-1 ring-brandorange" : "border-steel/20 hover:border-steel/50", blocked && "cursor-not-allowed opacity-40")}
              onClick={() => pick(k)}
            >
              <span className="block font-semibold text-ink">{LIEN_DOC_TITLE[k].replace("Waiver and Release ", "")}</span>
              <span className="text-steel">{blocked ? "Only once the money is received." : LIEN_WAIVER_WHEN[k]}</span>
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="w-36">
          <Label>Payment amount ($)</Label>
          <Input className="py-1 text-sm" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </div>
        {final ? (
          <div className="w-36">
            <Label>Disputed claims ($)</Label>
            <Input className="py-1 text-sm" inputMode="decimal" value={disputed} onChange={(e) => setDisputed(e.target.value)} />
          </div>
        ) : null}
        {kind === "conditional_final" ? (
          <div className="min-w-40 flex-1">
            <Label>Payment period</Label>
            <Input className="py-1 text-sm" value={period} maxLength={120} placeholder="e.g. Through October 31, 2026" onChange={(e) => setPeriod(e.target.value)} />
          </div>
        ) : null}
        <Button className="px-3 py-1.5 text-xs" busy={busy} onClick={() => void create()}>
          Make the waiver
        </Button>
      </div>
      {unconditional ? <p className="text-[11px] text-steel">Received on this invoice: ${dollars(invoice.amountPaidCents)}. An unconditional waiver can't be for more.</p> : null}
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      {rows.length ? (
        <div className="rounded-lg bg-white">
          {rows.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-2 border-b border-steel/10 px-3 py-1.5 text-sm last:border-0">
              <span className="min-w-0 flex-1 truncate text-ink">{r.templateName}</span>
              <span className={cn("text-xs", r.status === "signed" ? "text-success" : "text-steel")}>
                {r.status === "signed" ? `signed by ${r.signerName}` : r.status === "void" ? "void" : "not signed yet"}
              </span>
              {r.status === "pending" ? (
                <Button className="px-2.5 py-0.5 text-xs" onClick={() => setSigning(r)}>
                  Sign for company
                </Button>
              ) : null}
              <button className="text-xs text-steel underline hover:text-ink" onClick={() => void pdf(r)}>
                PDF
              </button>
            </div>
          ))}
        </div>
      ) : null}
      <p className="text-[11px] text-steel">Nevada's required wording (NRS 108.2457), filled from this invoice. Not legal advice: check with your attorney on anything unusual.</p>
      {signing ? <PresentAgreement tenantId={tenantId} agreement={signing} companyName={companyName} onExit={() => setSigning(null)} /> : null}
    </div>
  );
}
